import React, { useEffect, useState } from "react";
import DatePicker, { DateObject, Value } from "react-multi-date-picker";
import CalendarIcon from "../../../../assets/profile/icons/calendar.svg";
import { displayDate, formatToYYYYMMDD, parseFromYYYYMMDD } from "../utils";
import { IDateRange } from "../../../../store/api/affiliatesProfileApiSlice";
import { useTranslation } from "react-i18next";
import { days, months } from "../../constants";

export const AffiliatesDateRangePicker = ({
  dateRange,
  setDateRange,
}: {
  dateRange: IDateRange;
  setDateRange: React.Dispatch<React.SetStateAction<IDateRange>>;
}) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);

  const createDateObjectFromString = (dateString: string): DateObject => {
    try {
      return new DateObject(parseFromYYYYMMDD(dateString));
    } catch (error) {
      console.error("Error creating DateObject:", error);
      return new DateObject();
    }
  };

  const [values, setValues] = useState<Value[]>(() => {
    try {
      return [
        createDateObjectFromString(dateRange.start),
        createDateObjectFromString(dateRange.end),
      ];
    } catch (error) {
      console.error("Error initializing date values:", error);
      return [new DateObject(lastWeek), new DateObject(today)];
    }
  });

  const [firstSelection, setFirstSelection] = useState<DateObject | null>(null);

  useEffect(() => {
    try {
      const newStart = createDateObjectFromString(dateRange.start);
      const newEnd = createDateObjectFromString(dateRange.end);

      const currentStart =
        values[0] instanceof DateObject
          ? (values[0] as DateObject).format("YYYY-MM-DD")
          : "";
      const currentEnd =
        values[1] instanceof DateObject
          ? (values[1] as DateObject).format("YYYY-MM-DD")
          : "";

      if (dateRange.start !== currentStart || dateRange.end !== currentEnd) {
        setValues([newStart, newEnd]);
      }
    } catch (error) {
      console.error("Error updating values from dateRange:", error);
    }
  }, [dateRange.start, dateRange.end]);

  const handleSingleDateSelect = (dateObject: DateObject) => {
    if (!firstSelection) {
      setFirstSelection(dateObject);
    } else {
      const startDate = firstSelection;
      const endDate = dateObject;

      if (startDate.unix < endDate.unix) {
        setValues([startDate, endDate]);
        setDateRange({
          start: startDate.format("YYYY-MM-DD"),
          end: endDate.format("YYYY-MM-DD"),
        });
      } else {
        setValues([endDate, startDate]);
        setDateRange({
          start: endDate.format("YYYY-MM-DD"),
          end: startDate.format("YYYY-MM-DD"),
        });
      }

      setFirstSelection(null);
    }
  };

  const handleDateChange = (dateObjects: DateObject[] | null) => {
    if (dateObjects && dateObjects.length > 0) {
      if (dateObjects.length === 1) {
        handleSingleDateSelect(dateObjects[0]);
      } else if (dateObjects.length >= 2) {
        setValues([dateObjects[0], dateObjects[1]]);
        setDateRange({
          start: dateObjects[0].format("YYYY-MM-DD"),
          end: dateObjects[1].format("YYYY-MM-DD"),
        });
        setFirstSelection(null);
      }
    } else {
      const defaultValues = [new DateObject(lastWeek), new DateObject(today)];
      setValues(defaultValues);
      setDateRange({
        start: formatToYYYYMMDD(lastWeek),
        end: formatToYYYYMMDD(today),
      });
      setFirstSelection(null);
    }
  };

  return (
    <DatePicker
      range
      weekDays={isRTL ? days : undefined}
      months={isRTL ? months : undefined}
      format="YYYY-MM-DD"
      className="affiliates-date-rage-picker"
      value={values}
      onOpen={() => setFirstSelection(null)}
      onChange={handleDateChange}
      calendarPosition={i18n.dir() === "rtl" ? "bottom-right" : "bottom-left"}
      render={(_value, openCalendar) => {
        return (
          <div
            className="profile-affiliates-calendar-wrapper"
            onClick={openCalendar}
            style={{ cursor: "pointer" }}
          >
            <div>{displayDate(values, isRTL)}</div>
            <img src={CalendarIcon} alt="Calendar" />
          </div>
        );
      }}
    />
  );
};
